import { useState } from 'react';
import {
    Stack,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    CircularProgress,
    Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { useBolSync } from '../hooks/useBolConnector';
import { useToastStore } from '../../../../application/store/toastStore';
import { useImportJobsStore } from '../../../../application/store/importJobsStore';
import { ACTIVE_IMPORT_JOBS_QUERY_KEY } from '../../hooks/useActiveImportJobs';
import { APP_CONFIG } from '../../../../config/app.config';

interface SearchTermSyncProps {
    open: boolean;
    onClose: () => void;
    connectionId: string;
    connectionName: string;
}

export const SearchTermSync = ({ open, onClose, connectionId, connectionName }: SearchTermSyncProps) => {
    const queryClient = useQueryClient();
    const { syncSearchTerm } = useBolSync();
    const { addToast } = useToastStore();
    const addJob = useImportJobsStore((s) => s.addJob);

    const [searchTerm, setSearchTerm] = useState('');
    const [isStarting, setIsStarting] = useState(false);

    const handleClose = () => {
        if (isStarting) return;
        setSearchTerm('');
        onClose();
    };

    const handleSyncClick = async () => {
        const keyword = searchTerm.trim();
        if (!keyword) return;

        setIsStarting(true);
        try {
            const { jobId } = await syncSearchTerm({ connectionId, keyword });
            addJob({
                id: jobId,
                connector: 'bol',
                label: `${connectionName} · Search Term · ${keyword}`,
            });
            await queryClient.invalidateQueries({ queryKey: ACTIVE_IMPORT_JOBS_QUERY_KEY });
            addToast(`Search term sync started for "${keyword}"`, 'success');
            setSearchTerm('');
            onClose();
        } catch {
            addToast('Failed to start search term sync', 'error');
        } finally {
            setIsStarting(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
            <DialogTitle
                sx={{
                    borderBottom: `1px solid ${APP_CONFIG.ui.theme.colors.border}`,
                    p: 3,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <Typography variant="h6">Sync Search Term</Typography>
                <IconButton onClick={handleClose} disabled={isStarting}>
                    <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 3, mt: 3 }}>
                <Stack spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                        Fetch weekly search volume for a single Bol search term.
                    </Typography>
                    <TextField
                        label="Search term"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSyncClick();
                        }}
                        autoFocus
                        fullWidth
                        disabled={isStarting}
                    />
                </Stack>
            </DialogContent>
            <DialogActions sx={{ p: 3, borderTop: `1px solid ${APP_CONFIG.ui.theme.colors.border}` }}>
                <Button onClick={handleClose} variant="outlined" disabled={isStarting}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSyncClick}
                    disabled={isStarting || !searchTerm.trim()}
                    startIcon={isStarting ? <CircularProgress size={16} color="inherit" /> : undefined}
                >
                    {isStarting ? 'Starting…' : 'Sync'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
